/**
 * Wire → UI adapter for the review workspace.
 *
 * All backend format quirks live here: `warn`↔`warning`, 0-based page
 * indices → 1-based for the surface, `bbox_json` string parsing, KPI/citation
 * CSV splitting. Every consumer downstream sees a `Review` shape only —
 * wire types never escape this file.
 */

import {
  parseDetectorProfile,
  parseDetectorProfileResolved,
} from "@/components/domain/detector-profile"

import type {
  Authority,
  Evidence,
  Finding,
  FindingAxes,
  FindingGroup,
  FindingId,
  FindingPlacement,
  FindingSeverity,
  GoverningMaster,
  PointRect,
  Review,
  ReviewId,
} from "../data/review-data"
import type { WireFinding, WireFindingOccurrence, WireReview, WireSeverity } from "./wire"

/** Backend `warn` maps to our `warning`; other values pass through. */
export function severityFromWire(sev: WireSeverity): FindingSeverity {
  return sev === "warn" ? "warning" : sev
}

/**
 * Narrow to what `ManualFindingCreate` accepts: `^(error|warn)$`.
 *
 * Reads carry `info` as its own level, writes do not, so a finding filed at
 * `info` goes up as `error` rather than being refused at the boundary.
 */
export function severityToWire(sev: FindingSeverity): "error" | "warn" {
  return sev === "warning" ? "warn" : "error"
}

/** The inverse of the `+1` {@link parseBboxJson} applies: 1-based UI → 0-based wire. */
export function pageToWire(pageOneBased: number): number {
  return pageOneBased - 1
}

/** Parse `"[x0, y0, x1, y1]"`. Returns null on any parse failure. */
export function parseBboxJson(raw: string | null, pageZeroBased: number | null): PointRect | null {
  if (!raw || pageZeroBased == null) return null
  try {
    const arr = JSON.parse(raw) as unknown
    if (!Array.isArray(arr) || arr.length < 4) return null
    const [x0, y0, x1, y1] = arr
    if (
      typeof x0 !== "number" ||
      typeof y0 !== "number" ||
      typeof x1 !== "number" ||
      typeof y1 !== "number"
    ) {
      return null
    }
    return { page: pageZeroBased + 1, x0, y0, x1, y1 }
  } catch {
    return null
  }
}

/** Split `"40 CFR 156.10; LRM Ch 7"` into `[Authority, …]` with best-effort kinds. */
export function authoritiesFromCsv(csv: string | null): Authority[] {
  if (!csv) return []
  return csv
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean)
    .map<Authority>((label) => {
      const upper = label.toUpperCase()
      if (upper.startsWith("40 CFR") || upper.includes(" CFR ")) {
        return { kind: "cfr", label }
      }
      if (upper.startsWith("LRM")) return { kind: "lrm", label }
      if (upper.startsWith("PR NOTICE") || upper.startsWith("PRN")) {
        return { kind: "prn", label }
      }
      if (upper.startsWith("7 USC") || upper.includes(" USC ")) {
        return { kind: "statute", label }
      }
      return { kind: "internal", label }
    })
}

/** Front-panel/format rules go to g-format; text-diff rules go to g-text. */
function groupFromRule(ruleId: string): FindingGroup {
  const prefix = ruleId.split("-")[0]?.toUpperCase() ?? ""
  if (prefix === "TXT") {
    return { id: "g-text", label: "Text review", ordinal: 3 }
  }
  return { id: "g-format", label: "Formatting review", ordinal: 4 }
}

/**
 * Derive the three-way placement from the wire finding. `box` = has
 * coordinates; `page` = named a page but no box; `document` = neither.
 * This is the same contract as backend `placementOf` but implemented FE-side
 * since the wire response doesn't include a derived enum today.
 */
function placementFromWire(bbox: PointRect | null, page: number | null): FindingPlacement {
  if (bbox) return "box"
  if (page != null) return "page"
  return "document"
}

function axesFromWire(f: WireFinding): FindingAxes {
  // Backend SME verdict is confirm|reject|modify. `modify` is "SME accepted
  // with edits" — still a confirmation from the finding-validity perspective,
  // so we bucket it as `confirmed` here (the reviewer's edit is stored in
  // sme_note). "needs_sme" as a UI axis comes from expected_system_output,
  // not from sme_decision (which is *set* only after an SME acts).
  const verdict: FindingAxes["verdict"] =
    f.sme_decision === "confirm" || f.sme_decision === "modify"
      ? "confirmed"
      : f.sme_decision === "reject"
        ? "rejected"
        : f.expected_system_output === "needs_sme"
          ? "needs_sme"
          : "unset"

  const clientResolution: FindingAxes["clientResolution"] =
    f.client_resolution === "acknowledged"
      ? "acknowledged"
      : f.client_resolution === "disputed"
        ? "disputed"
        : f.client_resolution === "done"
          ? "done"
          : "none"

  return {
    verdict,
    state: f.state,
    clientResolution,
    origin: f.origin,
  }
}

function bboxFromPrimaryOccurrence(f: WireFinding): PointRect | null {
  // Prefer the FPL occurrence — the printed label is what the reviewer draws
  // on. Fall back to the top-level bbox_json if occurrences is empty (the
  // manual-add path posts these on the finding itself).
  const fplOcc: WireFindingOccurrence | undefined = f.occurrences.find((o) => o.side === "fpl")
  if (fplOcc) return parseBboxJson(fplOcc.bbox_json, fplOcc.page)
  return parseBboxJson(f.bbox_json, f.page)
}

/**
 * Every located printed-label occurrence, in the pipeline's order.
 *
 * Master-side occurrences are left out: the report steps through the printed
 * label, which is what the client corrects. A finding with no FPL occurrence
 * falls back to its top-level box, the same fallback `bbox` uses.
 */
function fplOccurrenceRects(f: WireFinding): PointRect[] {
  const rects = [...f.occurrences]
    .filter((o) => o.side === "fpl")
    .sort((a, b) => a.idx - b.idx)
    .map((o) => parseBboxJson(o.bbox_json, o.page))
    .filter((r): r is PointRect => r !== null)
  if (rects.length > 0) return rects
  const fallback = parseBboxJson(f.bbox_json, f.page)
  return fallback ? [fallback] : []
}

export function fromWireFinding(f: WireFinding, ordinal: number): Finding {
  const bbox = bboxFromPrimaryOccurrence(f)
  const placement = placementFromWire(bbox, f.page != null ? f.page + 1 : null)

  // Present when the pipeline named the difference, quoted either side of it,
  // or both — a statement with no quotes is still worth showing.
  const divergence: Evidence | null =
    f.master_text != null || f.fpl_text != null || f.divergence != null
      ? { master: f.master_text, printed: f.fpl_text, statement: f.divergence }
      : null

  return {
    id: String(f.id) as FindingId,
    number: ordinal,
    ruleId: f.rule_id,
    sectionId: f.section_id,
    severity: severityFromWire(f.severity),
    // A reviewer's own wording wins over the generated text: the override is
    // what the client report prints.
    claim: f.summary_override ?? f.message,
    claimIsOverridden: f.summary_override != null,
    placement,
    bbox,
    pageHint: f.page != null ? f.page + 1 : null,
    occurrences: Math.max(1, f.occurrences.length),
    occurrenceRects: fplOccurrenceRects(f),
    axes: axesFromWire(f),
    confidence: f.confidence,
    // Comma-separated on the wire, a list here: the console groups and filters
    // by KPI, and splitting at the edge keeps that string from travelling.
    kpis: (f.kpi_csv ?? "")
      .split(",")
      .map((k) => k.trim())
      .filter((k) => k.length > 0),
    // Backend doesn't return author name/at on findings today; leave null.
    // Manual findings can carry this once the endpoint is extended.
    authoredBy: null,
    group: groupFromRule(f.rule_id),
    kind: f.origin === "manual" ? "judgment" : "mechanical",
    divergence,
    reasoning: f.reasoning,
    remediation: f.remediation,
    authorities: authoritiesFromCsv(f.citation_csv),
    firstSeenVersion: f.carried_from_finding_id != null ? "carried" : "current",
    // These aren't on the review endpoint — messaging counts come from the
    // comments endpoint. Zero is the safe default until they merge.
    commentCount: 0,
    unreadCount: 0,
    smeNote: f.sme_note != null ? { by: "SME", at: "", note: f.sme_note } : null,
    dismissalNote: null,
  }
}

/**
 * Wire → UI review. Fields the wire doesn't carry today (assignment trail,
 * messages, client attachment) are filled with safe defaults so the surface
 * never crashes on a partial payload; those come from side calls
 * (`useReviewPeopleQuery`, comments query) merged in by the workspace.
 */
/**
 * Read the governing master out of the JSON string the wire carries it in.
 *
 * Narrowed field by field rather than cast: this is a text column the pipeline
 * writes, so a shape change there arrives as data, not as a type error, and a
 * cast would hand the console a half-built object to render. Anything
 * unreadable yields null, which the workspace already handles — a review can
 * legitimately have no master yet.
 */
export function parseGoverningMaster(raw: string | null): GoverningMaster | null {
  if (!raw) return null

  let value: unknown
  try {
    value = JSON.parse(raw)
  } catch {
    return null
  }
  // Arrays are objects to `typeof`, and an array here yields an all-null master
  // rather than the null that says "no master was established".
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null

  const o = value as Record<string, unknown>
  const text = (key: string): string | null => (typeof o[key] === "string" ? o[key] : null)

  // `stamp_date` is YYYYMMDD, which reads as a number to anyone who has not met
  // it before; the console shows dates one way.
  const stamp = text("stamp_date")
  const stampedOn =
    stamp?.length === 8 ? `${stamp.slice(0, 4)}-${stamp.slice(4, 6)}-${stamp.slice(6, 8)}` : null

  return {
    regNo: text("reg_nr"),
    stampedOn,
    acceptedOn: text("accepted_date"),
    needsSme: o["needs_sme"] === true,
    reasons: Array.isArray(o["reasons"])
      ? o["reasons"].filter((r): r is string => typeof r === "string")
      : [],
    url: text("url"),
  }
}

export function fromWireReview(w: WireReview): Review {
  const findings = w.findings.map((f, i) => fromWireFinding(f, i + 1))

  return {
    id: String(w.id) as ReviewId,
    epaRegNo: w.reg_nr,
    productName: w.product_name ?? w.reg_nr,
    productKind: "",
    version: `v${w.version_no}`,
    fplName: "fpl.pdf",
    masterName: "master.pdf",
    fplUrl: w.fpl_document_id != null ? `/api/v1/documents/${w.fpl_document_id}/file` : "",
    masterUrl: w.master_document_id != null ? `/api/v1/documents/${w.master_document_id}/file` : "",
    fplDate: w.fpl_date,
    // pageCount is not on the wire (backend PR gap #15 — documents.page_count).
    // pdf.js knows the true page count after it loads the document — the
    // PdfSurface component should update this from the parsed PDFDocumentProxy
    // once available; keep the placeholder at 1 so the paginator does not blow
    // up before the load resolves.
    pageCount: 1,
    governingMaster: parseGoverningMaster(w.governing_json ?? null),
    status: w.status,
    pipelineStage: w.stage,
    ocrNote: w.ocr_note,
    detectorProfileRequested: parseDetectorProfile(w.detector_profile_requested),
    detectorProfile: parseDetectorProfileResolved(w.detector_profile),
    unreadPagesHaveText: w.unread_pages_have_text,
    trashedAt: w.trashed_at,
    batchExpectedBy: w.batch_expected_by,
    previousReviewId: w.previous_review_id != null ? String(w.previous_review_id) : null,
    createdAt: w.created_at,
    updatedAt: w.completed_at ?? w.created_at,
    assignment: {
      currentStage: w.review_stage,
      archived: w.archived,
      secondarySkipped: w.secondary_skipped,
      assignee: null,
      events: [],
    },
    client: null,
    findings,
    messages: [],
  }
}
